
import moment from 'moment-timezone'
import { fetchEvents, fetchNearEvent } from '../../../utils/api'
import { throttle } from 'lodash'
import CalendarTemplate from './CalendarTemplate.vue'
import ModalAndBottomSheet from '../../../../components/ModalAndBottomSheet/ModalAndBottomSheet.vue'
import { gbLocalStorage } from '../../../../utils/storage'
import { eventHomework } from '../EventCard/mockEvents'

export default {
  components: { ModalAndBottomSheet, CalendarTemplate },

  props: {
    isInDashboard: {
      type: Boolean,
      default: false
    },

    isOnboardingMode: {
      type: Boolean,
      default: false
    },

    inWidget: {
      type: Boolean,
      default: false
    },

    useWithModals: {
      type: Boolean,
      default: false
    },

    firstDayOfWeek: {
      type: Array,
      default: null
    },

    cellSize: {
      type: String,
      default: 'small',
      validator: value => ['small', 'wide'].includes(value)
    }
  },

  data() {
    return {
      selectedDate: moment(),
      currentDate: moment(),
      daysNames: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
      eventDays: null,
      monthHolidays: null,
      activeEventsLoading: 0,
      selectOnOtherMonth: false,
      isModalVisible: false,
      eventFilters: {},
      nearEvent: null,
      selectedDay: {},
      onboardingHolidays: {
        from: '1995-01-01',
        id: 'onboarding_holidays',
        to: '2030-01-01'
      }
    }
  },

  computed: {
    calendar() {
      const startOfMonth = moment(this.currentDate).startOf('month')
      const endOfMonth = moment(this.currentDate).endOf('month')
      const startOfWeek = moment(startOfMonth).startOf('week')
      const endOfWeek = moment(endOfMonth).endOf('week')
      const numOfWeeks = endOfWeek.diff(startOfWeek, 'weeks')

      if (numOfWeeks <= 4) {
        endOfWeek.add(numOfWeeks === 4 ? 1 : 2, 'week')
      }

      const calendar = []
      let week = []
      let isWeekSelected = false
      let selectedWeek = null
      let selectedDay = null
      let today = null

      const checkIsSelected = day => {
        if (day.isSame(this.selectedDate, 'date')) {
          isWeekSelected = true

          return true
        }

        return false
      }

      const checkIsOtherMonth = checkDay => {
        return checkDay.isBefore(startOfMonth) || checkDay.isAfter(endOfMonth)
      }
      const day = moment(startOfWeek)
      const todayDate = moment().format('YYYY-MM-DD')
      // eslint-disable-next-line no-unmodified-loop-condition
      while (day <= endOfWeek) {
        for (let i = 0; i < 7; i++) {
          const [eventsDots, events, holiday] = this.getEventsForDay(day)

          const calendarDay = {
            totalDate: moment(day),
            date: day.date(),
            isOtherMonth: checkIsOtherMonth(day),
            today: day.isSame(moment(), 'day'),
            todayDate,
            isSelected: checkIsSelected(day),
            eventsDots,
            events,
            holidays: holiday ? this.monthHolidays : null
          }
          if (calendarDay.today) {
            today = calendarDay
          }

          if (calendarDay.isSelected) {
            selectedDay = calendarDay
          }
          week.push(calendarDay)
          day.add(1, 'day')
        }
        calendar.push(week)
        if (isWeekSelected) {
          selectedWeek = week
          isWeekSelected = false
        }
        week = []
      }

      return {
        data: calendar,
        selectedWeek,
        selectedDay,
        today,
        checkIsOtherMonth,
        fromTo: {
          from: startOfWeek.format('YYYY-MM-DD'),
          to: endOfWeek.format('YYYY-MM-DD')
        }
      }
    }
  },

  watch: {
    isOnboardingMode() {
      this.update()
    },

    async firstDayOfWeek() {
      await this.fetchEventsForCurrentMonth()
      this.checkDayAndSelect()
    },

    async currentDate() {
      const cacheSelectedDay = { ...this.calendar.selectedDay }
      await this.fetchEventsForCurrentMonth()

      if (this.selectOnOtherMonth) {
        // eslint-disable-next-line no-unused-vars
        const [_dots, events] = this.getEventsForDay(cacheSelectedDay?.totalDate)
        cacheSelectedDay.events = events
        cacheSelectedDay.weekDays = [...this.calendar.selectedWeek]
        this.nearEvent.isLoading = false
        this.emitDaySelection(cacheSelectedDay)
        this.selectOnOtherMonth = false
      }
    }
  },

  async mounted() {
    if (!this.inWidget) {
      window.addEventListener('set-local-active-filters', async event => {
        this.eventFilters = event.detail.storage
        await this.fetchNearEvent()
        this.update()
      })
    } else {
      await this.fetchNearEvent()
      this.update()
    }

    await this.fetchEventsForCurrentMonth()
    this.selectDay(this.calendar?.today)
  },

  beforeDestroy() {
    if (!this.inWidget) {
      window.removeEventListener('set-local-active-filters', async event => {
        this.eventFilters = event.detail.storage
        await this.fetchNearEvent()
        this.update()
      })
    }
  },

  methods: {
    sortEventsByHomework(events) {
      return (
        events?.sort((a, b) => {
          const aIsHomework = a?.type?.slug === 'homework'
          const bIsHomework = b?.type?.slug === 'homework'

          if (aIsHomework && !bIsHomework) {
            return -1
          }

          if (!aIsHomework && bIsHomework) {
            return 1
          }

          return 0
        }) || events
      )
    },

    selectToday() {
      this.selectedDate = moment()
      this.checkDayAndSelect()
    },

    addDays(numberOfDays) {
      this.selectedDate = moment(this.selectedDate.add(numberOfDays, 'day'))
      this.checkDayAndSelect()
    },

    subtractDays(numberOfDays) {
      this.selectedDate = moment(this.selectedDate.subtract(numberOfDays, 'day'))
      this.checkDayAndSelect()
    },

    checkDayAndSelect() {
      if (this.calendar.checkIsOtherMonth(this.selectedDate)) {
        this.selectOnOtherMonth = true
        this.setCurrentDate(moment(this.selectedDate))
      } else {
        const day = { ...this.calendar.selectedDay }
        day.weekDays = [...this.calendar.selectedWeek]
        this.emitDaySelection(day)
      }
    },

    update() {
      const dayToUpdate = { ...this.selectedDay }
      dayToUpdate.weekDays = this.selectedDay?.weekDays?.map(day => {
        const [dots, events, holidays] = this.getEventsForDay(day.totalDate)

        return { ...day, dots, events, holidays }
      })
      this.emitDaySelection(dayToUpdate)
    },

    emitDaySelection(day) {
      this.selectedDay = day
      this.$emit('select-day', {
        day,
        calendarData: this.calendar.data,
        nearEvent: this.nearEvent,
        monthHolidays: this.monthHolidays
      })
    },

    openModal() {
      this.isModalVisible = true
    },

    closeModal() {
      this.isModalVisible = false
    },

    setCurrentDate(day) {
      this.currentDate = day
    },

    toggleLoading(isLoading) {
      if (isLoading) {
        this.activeEventsLoading++
      } else {
        this.activeEventsLoading--
      }
    },

    fetchNearEvent: throttle(async function () {
      const excludeFilters = Object.keys(this.eventFilters)?.filter(e => {
        return !this.eventFilters[e]
      })
      const nearEvent = await fetchNearEvent(excludeFilters)
      this.nearEvent = this.createNearEvent(nearEvent?.period?.from)
    }, 1000),

    fetchEventsForCurrentMonth: throttle(async function () {
      this.toggleLoading(true)
      const { from, to } = this.calendar.fromTo
      const response = await fetchEvents(from, to)

      if (response) {
        moment.tz.setDefault(response.userTimeZone?.iana)
        this.eventDays = { ...this.eventDays, ...response.days }
        this.monthHolidays = Object.values(response?.holidays || {})[0]
        gbLocalStorage.set('calendar-filters', response.filters)
        window.dispatchEvent(new CustomEvent('set-local-storage-filters'))
      }
      this.toggleLoading(false)
    }, 1000),

    getEventsForDay(day) {
      const eventDots = new Set()
      if (this.eventDays) {
        const eventDay = this.eventDays[day?.format('YYYY-MM-DD')]
        const events = eventDay?.events?.filter(event => {
          return this.eventFilters[event.filterId] !== false
        })

        // add fakeEvents for onboarding
        if (this.isOnboardingMode) {
          const fakeHomework = {
            ...eventHomework,
            period: {
              from: null,
              to: day.date()
            },

            id: 'onboarding_homework' + day.date()
          }
          events.push(fakeHomework)
        }

        events?.forEach(e => {
          if (e.userRole === 'teacher') {
            eventDots.add('teacher')
          } else if (e.type.slug === 'lesson') {
            eventDots.add('lesson')
          } else if (e.type.slug === 'event') {
            eventDots.add('recommend')
          } else if (e.type.slug === 'homework') {
            eventDots.add('homework')
          }
        })

        if (eventDay?.holiday) {
          eventDots.add('holiday')
        }

        const sortedEvents = this.sortEventsByHomework(events)

        return [
          eventDots,
          sortedEvents,
          this.isOnboardingMode
            ? this.onboardingHolidays
            : eventDay?.holiday
              ? this.monthHolidays
              : null
        ]
      }

      return []
    },

    selectDay(day) {
      if (!day?.totalDate?.isSame(this.selectedDate) && !this.activeEventsLoading) {
        this.selectedDate = moment(day?.totalDate)
        this.checkDayAndSelect()
      }
    },

    createNearEvent(nearEventDate) {
      if (nearEventDate) {
        const momentEventDate = moment(nearEventDate)

        return {
          date: momentEventDate,
          isLoading: false,
          select: () => {
            if (this.calendar.checkIsOtherMonth(momentEventDate)) {
              this.nearEvent.isLoading = true
              this.selectOnOtherMonth = true
              this.currentDate = moment(momentEventDate)
              this.selectedDate = moment(momentEventDate)
            } else {
              this.selectedDate = moment(momentEventDate)
              const selectedDay = this.calendar.selectedDay

              if (selectedDay) {
                selectedDay.weekDays = [...this.calendar.selectedWeek]
              }

              this.emitDaySelection(selectedDay)
            }
          }
        }
      }

      return null
    }
  }
}
